import { useState } from 'react';
import PropTypes from 'prop-types';
import { createFeedbackTicket } from '../api/odoo';
import { safeErrorMessage } from '../utils/errorMessage';
import usePassengerDetails from '../hooks/usePassengerDetails';
import PassengerDetailsCard from '../components/PassengerDetailsCard';
import '../styles/form-page.css';
import './FeedbackPage.css';

const COLS = ['BAD', 'OKAY', 'GOOD', 'GREAT', 'EXC.'];
const COL_COLORS = ['#e53e3e', '#dd6b20', '#d69e2e', '#38a169', '#3182ce'];
const ROWS = [
  { key: 'serviceQuality', label: 'Service' },
  { key: 'cleanliness',    label: 'Cleanliness' },
  { key: 'food',           label: 'Food' },
  { key: 'staff',          label: 'Staff' },
  { key: 'roomAmenities',  label: 'Room' },
];

export default function FeedbackPage({ partner, onBack, onSuccess }) {
  const details = usePassengerDetails();
  const [ratings, setRatings] = useState({ serviceQuality: 0, cleanliness: 0, food: 0, staff: 0, roomAmenities: 0 });
  const [overall, setOverall] = useState(0);
  const [suggestion, setSuggestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const { values, isComplete: step1Done } = details;

  async function submit() {
    if (!step1Done) { setShowErrors(true); setError('Please fill in all required fields.'); return; }
    if (!agreed) { setError('Please agree to the declaration before submitting.'); return; }
    setLoading(true); setError('');
    try {
      const ticket = await createFeedbackTicket(partner, {
        ratings,
        overallRating: overall,
        suggestion,
        ticketNo: values.ticketNo,
        pnr: values.pnr,
      });
      onSuccess({ ticketId: ticket.id, ticketName: ticket.name, type: 'feedback' });
    } catch (e) {
      if (e.alreadySubmitted) {
        onSuccess({ ticketId: e.ticketId, ticketName: e.ticketName, type: 'feedback', alreadySubmitted: true });
        return;
      }
      setError(safeErrorMessage(e, 'Submission failed.'));
      setLoading(false);
    }
  }

  return (
    <div className="fp">
      <div className="fp-nav">
        <button type="button" className="fp-back" onClick={onBack}>‹</button>
        <div><p className="fp-nav-t">Give Feedback</p><p className="fp-nav-s">Rate your experience</p></div>
      </div>

      <div className="fp-body">

        <PassengerDetailsCard classPrefix="fp" partner={partner} details={details} showErrors={showErrors} />

        {step1Done && (
          <>
            <div className="fp-card">
              <div className="fp-ch"><div className="fp-ci">⭐</div><span className="fp-ct">Service Rating</span><span className="fp-hint">Tap to rate</span></div>
              <div className="fp-grid">
                <div className="fp-ghd" aria-hidden>
                  <div />
                  {COLS.map((c, i) => <div key={c} className="fp-gcol" style={{ color: COL_COLORS[i] }}>{c}</div>)}
                </div>
                {ROWS.map(({ key, label }) => (
                  <div key={key} className="fp-gr">
                    <div className="fp-glbl"><span className="fp-gdot" />{label}</div>
                    {[1, 2, 3, 4, 5].map(v => (
                      <button key={v} type="button" className={`fp-gs${ratings[key] >= v ? ' on' : ''}`}
                        aria-label={`Rate ${label} ${v} out of 5`}
                        onClick={() => setRatings(p => ({ ...p, [key]: v }))}>
                        {ratings[key] >= v ? '★' : '☆'}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div className="fp-card">
              <div className="fp-ch"><div className="fp-ci">💡</div><span className="fp-ct">Overall Flight Experience</span><span className="fp-hint">Tap to rate</span></div>
              <div className="fp-ovr">
                {[1, 2, 3, 4, 5].map(v => (
                  <button key={v} type="button" className={`fp-ovs${overall >= v ? ' on' : ''}`}
                    aria-label={`Rate overall experience ${v} out of 5`}
                    onClick={() => setOverall(v)}>
                    {overall >= v ? '★' : '☆'}
                  </button>
                ))}
              </div>
            </div>

            <div className="fp-card">
              <div className="fp-ch"><div className="fp-ci">💬</div><span className="fp-ct">Suggestions / Comments</span></div>
              <textarea className="fp-txt" rows={3} placeholder="Share your suggestions or comments…"
                value={suggestion} onChange={e => setSuggestion(e.target.value)} />
            </div>
          </>
        )}

        {!step1Done && (
          <div className="fp-step-hint">
            <span aria-hidden>👆</span>
            <p>Fill in passenger details above to unlock the rating form</p>
          </div>
        )}

        {error && <p className="fp-err">{error}</p>}
      </div>

      <div className="fp-foot">
        <label className="fp-agree">
          <input type="checkbox" className="fp-agree-cb" checked={agreed} onChange={e => { setAgreed(e.target.checked); setError(''); }} />
          <span>I confirm that the information provided is accurate.</span>
        </label>
        <button type="button" className="fp-sub" onClick={submit} disabled={loading || !step1Done || !agreed}>
          {loading ? 'Submitting…' : '🚀 Submit Feedback'}
        </button>
      </div>
    </div>
  );
}

FeedbackPage.propTypes = {
  partner: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    hotel_name: PropTypes.string,
  }).isRequired,
  onBack: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};
